import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  Circle,
  NaverMap,
  Marker,
  RenderAfterNavermapsLoaded,
} from "react-naver-maps";

import { getDistance, getIntensityFromMag } from "../utils";

const NAVER_MAP_CLIENT_ID = "eqru4ypgij";
const NAVER_MAP_INITIAL_ZOOM_LEVEL = 7;

const P_WAVE_SPEED = 5500; // 5500 m/sec
const S_WAVE_SPEED = 3500; // 3500 m/sec

export const IntensitySimulationMap = ({ simulationParameters }) => {
  const [elapsedTime, setElapsedTime] = useState(0);
  const [arrivalTime, setArrivalTime] = useState(0);
  const [estimatedIntensity, setEstimatedIntensity] = useState("?");
  const [estimatedShock, setEstimatedShock] = useState("?");

  const intervalRef = useRef(null);

  const { t } = useTranslation();

  useEffect(() => {
    if (
      simulationParameters &&
      simulationParameters.earthquake &&
      simulationParameters.user
    ) {
      const { earthquake, user } = simulationParameters;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      setElapsedTime(0);
      const distanceInKm = getDistance(
        earthquake.latitude,
        earthquake.longitude,
        user.latitude,
        user.longitude
      );
      setArrivalTime((distanceInKm * 1000) / S_WAVE_SPEED);
      const intensity = getIntensityFromMag(
        earthquake.magnitude,
        distanceInKm,
        earthquake.depth
      );
      setEstimatedIntensity(intensity);
      const shock =
        intensity <= 2
          ? "미세"
          : intensity <= 4
          ? "약한"
          : intensity <= 5
          ? "강한"
          : "격한";
      setEstimatedShock(shock);
      intervalRef.current = setInterval(() => {
        setElapsedTime((t) => t + 1);
        setArrivalTime((t) => t - 1);
      }, 1000);

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      };
    }
  }, [simulationParameters]);

  // useEffect(() => {
  //   if (intervalRef.current && elapsedTime > INTENSITY_SIMULATION_PLAY_TIME) {
  //     clearInterval(intervalRef.current);
  //     intervalRef.current = null;
  //   }
  // }, [elapsedTime]);

  useEffect(() => {
    if (intervalRef.current && arrivalTime < -9) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, [arrivalTime]);

  return (
    <div className="map-container">
      <div
        id="map"
        style={{ width: "100%", height: "800px", overflow: "hidden" }}
      >
        <RenderAfterNavermapsLoaded
          govClientId={NAVER_MAP_CLIENT_ID}
          error={<p>Maps Load Error</p>}
          loading={<p>Maps Loading...</p>}
        >
          <NaverMap
            style={{ width: "100%", height: "100%", overflow: "hidden" }}
            zoom={NAVER_MAP_INITIAL_ZOOM_LEVEL}
            // center={{
            //   lat: simulationParameters.earthquake.latitude,
            //   lng: simulationParameters.earthquake.longitude,
            // }}
          >
            {simulationParameters?.earthquake && (
              <Marker
                icon={{
                  url: "https://kr.object.gov-ncloudstorage.com/kerc-kquake-public/web/images/icons/marker_earthquake.png",
                }}
                position={{
                  lat: simulationParameters.earthquake.latitude,
                  lng: simulationParameters.earthquake.longitude,
                }}
                // draggable={true}
                // events={["click", "position_changed"]}
                // onPositionChanged={(event) => console.log(event)}
                title="지진 발생 위치"
              />
            )}
            {simulationParameters?.user && (
              <Marker
                position={{
                  lat: simulationParameters.user.latitude,
                  lng: simulationParameters.user.longitude,
                }}
                title="내 위치"
              />
            )}
            {simulationParameters?.earthquake && elapsedTime > 0 && (
              <>
                <Circle
                  center={{
                    lat: simulationParameters.earthquake.latitude,
                    lng: simulationParameters.earthquake.longitude,
                  }}
                  radius={elapsedTime * P_WAVE_SPEED}
                  strokeWeight={3}
                  strokeColor="#0000FF"
                  strokeOpacity={0.5}
                />
                <Circle
                  center={{
                    lat: simulationParameters.earthquake.latitude,
                    lng: simulationParameters.earthquake.longitude,
                  }}
                  radius={elapsedTime * S_WAVE_SPEED}
                  strokeWeight={3}
                  strokeColor="#FF0000"
                  strokeOpacity={0.5}
                />
              </>
            )}
          </NaverMap>
        </RenderAfterNavermapsLoaded>
      </div>
      <div className="map-controller label_white">
        <div className="inline-block" style={{ width: 85 }}>
          <div
            style={{
              marginBottom: -2,
              padding: 5,
              border: "2px solid #A9A9A9",
              borderTopLeftRadius: 5,
              borderTopRightRadius: 5,
              textAlign: "center",
            }}
          >
            {t("page.intensity-simulation.estimatedIntensity")}
          </div>
          <div
            style={{
              padding: 10,
              border: "2px solid #A9A9A9",
              borderBottomLeftRadius: 5,
              borderBottomRightRadius: 5,
              textAlign: "center",
              fontSize: 40,
              color:
                estimatedIntensity === "?" || estimatedIntensity <= 4
                  ? "#0000FF"
                  : "#FF0000",
            }}
          >
            {estimatedIntensity}
          </div>
        </div>
        <div className="inline-block" style={{ width: 110, marginLeft: -2 }}>
          <div
            style={{
              marginBottom: -2,
              padding: 5,
              border: "2px solid #A9A9A9",
              borderTopLeftRadius: 5,
              borderTopRightRadius: 5,
              textAlign: "center",
            }}
          >
            {t("page.intensity-simulation.estimatedShock")}
          </div>
          <div
            style={{
              height: 84,
              padding: 10,
              border: "2px solid #A9A9A9",
              borderBottomLeftRadius: 5,
              borderBottomRightRadius: 5,
              textAlign: "center",
              fontSize: 40,
              color:
                estimatedIntensity === "?" || estimatedIntensity <= 4
                  ? "#0000FF"
                  : "#FF0000",
            }}
          >
            {estimatedShock}
          </div>
        </div>
        <div className="inline-block" style={{ width: 85, marginLeft: -2 }}>
          <div
            style={{
              marginBottom: -2,
              padding: 5,
              border: "2px solid #A9A9A9",
              borderTopLeftRadius: 5,
              borderTopRightRadius: 5,
              textAlign: "center",
            }}
          >
            {t("page.intensity-simulation.arrivalTime")}
          </div>
          <div
            style={{
              padding: 10,
              border: "2px solid #A9A9A9",
              borderBottomLeftRadius: 5,
              borderBottomRightRadius: 5,
              textAlign: "center",
              fontSize: 40,
              color:
                elapsedTime > 0 && arrivalTime <= 5 ? "#FF0000" : "#000000",
            }}
          >
            {/* {arrivalTime > 0 ? (arrivalTime - elapsedTime).toFixed(0) : "?"} */}
            {arrivalTime.toFixed(0)}
          </div>
        </div>
      </div>
      <div className="map-controller-bottom label_white">
        ※ {t("page.intensity-simulation.descInMap1")}
        <br />
        {t("page.intensity-simulation.descInMap2")}
      </div>
    </div>
  );
};
